import React from 'react';
import { AmendmentEntry } from './AmendmentHistory';

export interface AmendmentHistoryView {
    /** False when there is nothing to show: switched off, or no history. */
    visible: boolean;
    entries: AmendmentEntry[];
    loading: boolean;
    error: boolean;
}

interface LoadState {
    entries: AmendmentEntry[];
    loading: boolean;
    error: boolean;
}

const IDLE: LoadState = { entries: [], loading: false, error: false };

/**
 * Loads one history for AmendmentHistory: a submission's (SKIN-602) or a
 * patient's personal data (SKIN-374). `key` names what is loaded and reloads
 * it when it changes; null means the history is off here, so nothing is asked
 * and nothing shows. `load` fetches and maps (decrypting where it can).
 * `visible` stays false for an empty history, so a never-edited record looks
 * as before.
 */
export function useAmendmentHistory(key: string | null, load: () => Promise<AmendmentEntry[]>): AmendmentHistoryView {
    const loadRef = React.useRef(load);
    loadRef.current = load;
    const [state, setState] = React.useState<LoadState>(IDLE);

    React.useEffect(() => {
        if (key === null) {
            setState(IDLE);
            return undefined;
        }
        let cancelled = false;
        setState({ entries: [], loading: true, error: false });
        loadRef.current()
            .then((entries) => { if (!cancelled) setState({ entries, loading: false, error: false }); })
            .catch(() => { if (!cancelled) setState({ entries: [], loading: false, error: true }); });
        return () => { cancelled = true; };
    }, [key]);

    return { ...state, visible: key !== null && (state.loading || state.error || state.entries.length > 0) };
}
